import { i18next } from '@frontend/i18n';
import { ValidatorCommonErrors } from './types';

const t = i18next.getFixedT(null, 'content-composer');

// Common error codes should be above 100 to avoid collisions
// with section-specific error codes, which typically start at 1.
export const CommonErrors = {
  UnknownError: {
    code: 100,
    details: t('An unknown validation error occurred.'),
    summary: (count) => t('{{count}} unknown validation errors', { count }),
  },
  MultiMissingPhone: {
    code: 101,
    details: t(
      'One or more locations have not selected a phone number in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing a phone number', { count }),
  },
  MultiMissingAddress: {
    code: 102,
    details: t(
      'One or more locations have not set an address in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations without an address', { count }),
  },
  MultiMissingSchedule: {
    code: 103,
    details: t('One or more locations have not properly configured their schedule yet.'),
    summary: (count: number) => t('{{count}} locations need schedule configuration', { count }),
  },
  MultiMissingEmail: {
    code: 104,
    details: t('One or more locations have not defined an email address. Inspect this element to resolve this issue.'),
    summary: (count: number) => t('{{count}} locations missing an email address', { count }),
  },
  MultiMissingForm: {
    code: 105,
    details: t(
      'One or more locations do not have access to this form or packet. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing form or packet', { count }),
  },
  MultiMissingGoogle: {
    code: 106,
    details: t(
      'One or more locations have not connected to Google in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing Google connection', { count }),
  },
  MultiMissingFacebook: {
    code: 107,
    details: t(
      'One or more locations have not connected to Facebook in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing Facebook connection', { count }),
  },
  MultiMissingCustomReview: {
    code: 108,
    details: t(
      'One or more locations have not set a custom reviews link in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing custom review link', { count }),
  },
  MultiMissingWebPage: {
    code: 109,
    details: t(
      'One or more locations have not set a link to their web page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing web page link', { count }),
  },
  MultiMissingContactPage: {
    code: 110,
    details: t(
      'One or more locations have not set a link to their contact page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing contact page link', { count }),
  },
  MultiMissingAboutPage: {
    code: 111,
    details: t(
      'One or more locations have not set a link to their about page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing about page link', { count }),
  },
  MultiMissingShop: {
    code: 112,
    details: t(
      'One or more locations have not set a link to their shop in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing shop link', { count }),
  },
  MultiMissingPromotionalPage: {
    code: 113,
    details: t(
      'One or more locations have not set a link to their promotional page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing promotional page link', { count }),
  },
  MultiMissingFaqPage: {
    code: 114,
    details: t(
      'One or more locations have not set a link to their FAQ page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing FAQ page link', { count }),
  },
  MultiMissingBlog: {
    code: 115,
    details: t(
      'One or more locations have not set a link to their blog in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing blog link', { count }),
  },
  MultiMissingHelpCenter: {
    code: 116,
    details: t(
      'One or more locations have not set a link to their help center in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing help center link', { count }),
  },
  MultiMissingMap: {
    code: 117,
    details: t(
      'One or more locations have not set a link to their map in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing map link', { count }),
  },
  MultiMissingReferralPage: {
    code: 118,
    details: t(
      'One or more locations have not set a link to their referral page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing referral page link', { count }),
  },
  MultiMissingOther: {
    code: 119,
    details: t(
      'One or more locations have not set a link to their custom other page in Brand Settings. Inspect this element to resolve this issue.'
    ),
    summary: (count: number) => t('{{count}} locations missing other page link', { count }),
  },
} as const satisfies ValidatorCommonErrors;

type CommonErrorCode = (typeof CommonErrors)[keyof typeof CommonErrors]['code'];

const commonErrorCodes = new Set<number>(Object.values(CommonErrors).map((error) => error.code));

export const isCommonErrorCode = (errorCode: number): errorCode is CommonErrorCode => {
  return commonErrorCodes.has(errorCode);
};
